// Listing detail gallery - a single continuous-scroll slider: the photo mosaic
// (1 big + 2 small, repeating) plus a Video section plus a Location/map section
// all live side-by-side inside one horizontal scroll-snap track
// ([data-role="gallery-track"]). Native swipe / trackpad scroll flows straight
// from the last photo into the video and location sections.
// Tabs (Fotografii / Video / Locație) are quick-jump anchors: a click scroll-
// snaps the track to that section, and the active tab is kept in sync with the
// visible section via an IntersectionObserver. Video/map iframes lazy-load when
// their section scrolls into view. Clicking a photo tile opens the shared
// lightbox seeded with the photo set. Tabs are server-rendered only for
// sections that have data.
import { qs, qsa, on, delegate, lockScroll, trapFocus } from '../lib/dom.js';
import { t } from '../lib/i18n.js';

export function initGallery() {
  const root = qs('[data-component="gallery"]');
  const lightbox = qs('#ap-lightbox');
  if (!root) return;

  let lightboxIndex = 0;   // current image in the lightbox
  let lbSet = [];          // photo set backing the open lightbox
  let releaseFocus = null;
  const lbPreloaded = new Set(); // full-size URLs already fetched/decoded
  let lbCurrentFull = '';        // guards against out-of-order async swaps

  const lbImage = lightbox ? qs('[data-role="lightbox-image"]', lightbox) : null;
  const lbCounter = lightbox ? qs('[data-role="lightbox-counter"]', lightbox) : null;
  const lbThumbs = lightbox ? qs('[data-role="lightbox-thumbs"]', lightbox) : null;
  const lbVideoHost = lightbox ? qs('[data-role="lightbox-video"]', lightbox) : null;
  const stage = lightbox ? qs('.ap-lightbox__stage', lightbox) : null;
  const lbZoomCtl = lightbox ? qs('[data-role="lightbox-zoom"]', lightbox) : null;

  // Optional car video → appended as the LAST lightbox slide. Built from the
  // gallery root's data attributes (see listing-title.php). `embed` already
  // carries autoplay=1; the iframe is created lazily only when its slide opens.
  const videoEmbed = root.dataset.videoEmbed || '';
  const videoPoster = root.dataset.videoPoster || '';
  // Vertical (YouTube Shorts) clips render in a 9:16 portrait box so the video
  // fills the stage height instead of pillarboxing into a narrow 16:9 strip -
  // the larger render lets YouTube auto-serve a higher quality than 360p.
  const videoPortrait = root.dataset.videoPortrait === '1';
  const videoSlide = videoEmbed
    ? { type: 'video', embed: videoEmbed, thumb: videoPoster, full: videoPoster }
    : null;
  // Append the video as the last slide of a photo set (or a lone slide when the
  // car has photos-less video). Returns a fresh array so callers never mutate
  // the server-rendered photo list.
  function withVideo(photos) {
    return videoSlide ? photos.concat(videoSlide) : photos;
  }
  const isVideoItem = (item) => !!item && item.type === 'video';

  // Build / tear down the lightbox video iframe. Lazy: the iframe only exists
  // while the video slide is the active one, so audio never leaks when the user
  // swipes away or closes the modal.
  function mountVideoFrame() {
    if (!lbVideoHost || !videoSlide) return;
    lbVideoHost.hidden = false;
    lbVideoHost.classList.toggle('is-portrait', videoPortrait);
    if (lbImage) lbImage.style.visibility = 'hidden';
    if (!qs('iframe', lbVideoHost)) {
      const frame = document.createElement('iframe');
      // iOS Safari blocks UNMUTED autoplay in an iframe even after a tap, so the
      // video would just sit on its poster. Mute on iOS so it always starts
      // playing (YouTube shows its own unmute control); desktop keeps sound.
      let src = videoSlide.embed;
      const isIOS = /iP(ad|hone|od)/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (isIOS && !/[?&]mute=1(?:&|$)/.test(src)) src += '&mute=1';
      frame.src = src;
      frame.title = 'Video';
      frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      frame.allowFullscreen = true;
      lbVideoHost.appendChild(frame);
    }
  }
  function unmountVideoFrame() {
    if (!lbVideoHost) return;
    lbVideoHost.hidden = true;
    lbVideoHost.innerHTML = ''; // stops playback + audio immediately
    if (lbImage) lbImage.style.visibility = '';
  }

  // The REAL thumbnail URL of a grid <img>. A page-cache lazy-loader (WP Rocket
  // LazyLoad on staging/prod) rewrites every deferred `loading="lazy"` tile —
  // i.e. every tile past the first few eager ones — swapping its `src` for a
  // 1x1/SVG placeholder and stashing the true URL in `data-lazy-src` (WP Rocket)
  // or `data-src` (generic). Reading `src` alone therefore yields the real photo
  // for the first ~5 eager tiles but a blank placeholder for the rest, which is
  // exactly why the later lightbox thumbnails render empty. Prefer the lazy-load
  // data attribute; only fall back to a `src` that is an actual http(s) URL.
  function realImgSrc(img) {
    if (!img) return '';
    const lazy = img.getAttribute('data-lazy-src') || img.getAttribute('data-src');
    if (lazy) return lazy;
    const src = img.getAttribute('src') || '';
    // Ignore inline placeholders (data: URIs) left by a lazy-loader.
    return src.slice(0, 5) === 'data:' ? '' : src;
  }

  // Read a grid's photos from its server-rendered tiles (thumb in <img>,
  // full-size URL in data-full). One grid per photo panel. `data-full` is a
  // plain attribute the lazy-loader never touches, so it is the safe fallback
  // when a deferred tile's <img> src is still a placeholder.
  function photosOf(grid) {
    if (!grid) return [];
    return qsa('.ap-cd-gallery-tile', grid).map((tile) => {
      const full = tile.dataset.full || '';
      const thumb = realImgSrc(qs('img', tile)) || full;
      return { thumb, full: full || thumb };
    });
  }

  // ---- Continuous-scroll track (photos + video + location as one slider) ----
  // The outer track scrolls section-by-section (Fotografii → Video → Locație).
  // The photos section keeps its OWN inner mosaic scroll; the arrows/tabs bridge
  // the two so navigation feels like one continuous slider.
  const track = qs('[data-role="gallery-track"]', root);
  const photoGrid = qs('[data-role="gallery-grid"]', root); // inner mosaic scroller (may be null: no photos)
  const sections = qsa('.ap-cd-gallery-panel', track || root);

  // Lazy-attach the iframe (video / location fallback) inside a section when it
  // first becomes visible. The JS-API map (.ap-gmap) self-inits via its own
  // IntersectionObserver in atpl-maps.js, so this only wires up the iframe path.
  function activateSection(panel) {
    if (!panel) return;
    const iframe = qs('iframe[data-src]', panel);
    if (iframe && !iframe.src) iframe.src = iframe.dataset.src;
  }

  // How many "pages" the inner photo mosaic has, and where it currently sits.
  const photoAtStart = () => !photoGrid || photoGrid.scrollLeft <= 2;
  const photoAtEnd = () =>
    !photoGrid || photoGrid.scrollLeft + photoGrid.clientWidth >= photoGrid.scrollWidth - 2;

  // Scroll the outer track so `panel` is the visible section. Uses scrollLeft
  // (not scrollIntoView) to avoid scrolling the whole page vertically.
  function scrollToSection(panel) {
    if (!track || !panel) return;
    track.scrollTo({ left: panel.offsetLeft, behavior: 'smooth' });
    activateSection(panel);
  }

  const currentSectionIndex = () => {
    if (!track) return 0;
    const x = track.scrollLeft;
    let best = 0, bestDist = Infinity;
    sections.forEach((p, i) => {
      const d = Math.abs(p.offsetLeft - x);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    return best;
  };

  const isPhotoSection = (i) => sections[i] && sections[i].dataset.panel === 'body';

  // Prev/next: step the inner photo mosaic first (when we're on the photo
  // section and it still has room to scroll), otherwise move the outer track to
  // the neighbouring section. This makes one arrow chain photos → video →
  // location seamlessly.
  function step(dir) {
    const idx = currentSectionIndex();
    if (isPhotoSection(idx) && photoGrid) {
      if (dir > 0 && !photoAtEnd()) {
        photoGrid.scrollBy({ left: photoGrid.clientWidth, behavior: 'smooth' });
        return;
      }
      if (dir < 0 && !photoAtStart()) {
        photoGrid.scrollBy({ left: -photoGrid.clientWidth, behavior: 'smooth' });
        return;
      }
    }
    const next = idx + dir;
    if (next >= 0 && next < sections.length) scrollToSection(sections[next]);
  }

  // Show/hide arrows when there is nothing to navigate to in that direction.
  function updateArrows() {
    const idx = currentSectionIndex();
    const canPrev = idx > 0 || (isPhotoSection(idx) && photoGrid && !photoAtStart());
    const canNext = idx < sections.length - 1 || (isPhotoSection(idx) && photoGrid && !photoAtEnd());
    const single = sections.length <= 1 && (!photoGrid || photoGrid.scrollWidth <= photoGrid.clientWidth + 4);
    qsa('[data-action="gallery-prev"]', root).forEach((b) => {
      b.disabled = single || !canPrev;
      b.style.display = single ? 'none' : '';
    });
    qsa('[data-action="gallery-next"]', root).forEach((b) => {
      b.disabled = single || !canNext;
      b.style.display = single ? 'none' : '';
    });
  }

  delegate(root, 'click', '[data-action="gallery-prev"]', (e) => { e.preventDefault(); step(-1); });
  delegate(root, 'click', '[data-action="gallery-next"]', (e) => { e.preventDefault(); step(1); });

  // ---- Touch bridge: swipe past the last/first photo → next/prev section ----
  // On mobile the arrows are hidden and the inner photo mosaic is its own
  // horizontal scroller nested inside the outer track. Browsers do NOT chain a
  // touch swipe from a nested overflow-x scroller to its ancestor, so once the
  // inner mosaic hits its edge the swipe just dead-ends. This handler detects a
  // horizontal swipe that starts already at the mosaic's end (or start) and
  // jumps the outer track to the neighbouring section. Section DOM order is
  // photos → video → location (only rendered when data exists), so "next" lands
  // on the video panel when a video is present and on the location panel
  // otherwise — the desired video-then-location fallback.
  if (photoGrid && track && sections.length > 1) {
    const HSWIPE = 45;   // min horizontal travel to count as a swipe (px)
    const VBIAS  = 1.3;  // horizontal must dominate vertical by this factor
    const OVER   = 42;   // past-edge drag (px) that flips to the neighbouring tab
    let tsX = 0, tsY = 0, lastX = 0, overEnd = 0, overStart = 0;
    let atEndStart = false, atStartStart = false, tracking = false, fired = false;

    on(photoGrid, 'touchstart', (e) => {
      if (!e.touches || e.touches.length !== 1 || !isPhotoSection(currentSectionIndex())) { tracking = false; return; }
      tsX = lastX = e.touches[0].clientX;
      tsY = e.touches[0].clientY;
      atEndStart = photoAtEnd();
      atStartStart = photoAtStart();
      overEnd = overStart = 0;
      tracking = true;
      fired = false;
    }, { passive: true });

    // Bridge DURING the swipe: once the mosaic is pinned at an edge, tally how far
    // the finger keeps dragging past it and cross to the next/prev tab the moment
    // that exceeds OVER — so you never wait for the momentum/snap to settle.
    on(photoGrid, 'touchmove', (e) => {
      if (!tracking || fired || !e.touches || e.touches.length !== 1) return;
      const x = e.touches[0].clientX;
      const step = x - lastX;
      lastX = x;
      if (step < 0 && photoAtEnd()) { overEnd += -step; overStart = 0; }
      else if (step > 0 && photoAtStart()) { overStart += step; overEnd = 0; }
      else { overEnd = overStart = 0; }
      const idx = currentSectionIndex();
      if (overEnd >= OVER && idx < sections.length - 1) { fired = true; scrollToSection(sections[idx + 1]); updateArrows(); }
      else if (overStart >= OVER && idx > 0) { fired = true; scrollToSection(sections[idx - 1]); updateArrows(); }
    }, { passive: true });

    // Fallback for a quick flick that ended right at the edge without accruing
    // enough in-gesture overscroll: a clean horizontal swipe that STARTED at the edge.
    on(photoGrid, 'touchend', (e) => {
      if (!tracking || fired) { tracking = false; return; }
      tracking = false;
      const touch = (e.changedTouches && e.changedTouches[0]) || null;
      if (!touch) return;
      const dx = touch.clientX - tsX;
      const dy = touch.clientY - tsY;
      if (Math.abs(dx) < HSWIPE || Math.abs(dx) < Math.abs(dy) * VBIAS) return;
      const idx = currentSectionIndex();
      if (!isPhotoSection(idx)) return;
      if (dx < 0 && atEndStart && idx < sections.length - 1) { scrollToSection(sections[idx + 1]); updateArrows(); }
      else if (dx > 0 && atStartStart && idx > 0) { scrollToSection(sections[idx - 1]); updateArrows(); }
    }, { passive: true });
  }

  // ---- Active-tab sync on scroll ----
  function setActiveTab(panelKey) {
    qsa('.ap-cd-gallery-tab', root).forEach((tab) => {
      const on2 = tab.dataset.tab === panelKey;
      tab.classList.toggle('is-active', on2);
      tab.setAttribute('aria-selected', String(on2));
    });
  }

  // Highlight the tab of whichever section is centred in the track. Prefer an
  // IntersectionObserver (debounced by the browser); fall back to a scroll
  // listener when unavailable.
  if (track && sections.length) {
    if (typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver((entries) => {
        // Pick the most-visible section still intersecting.
        let top = null;
        entries.forEach((en) => {
          if (en.isIntersecting && (!top || en.intersectionRatio > top.intersectionRatio)) top = en;
        });
        if (top) {
          setActiveTab(top.target.dataset.panel);
          activateSection(top.target);
        }
      }, { root: track, threshold: [0.25, 0.5, 0.75] });
      sections.forEach((p) => io.observe(p));
    } else {
      let raf = 0;
      on(track, 'scroll', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const p = sections[currentSectionIndex()];
          if (p) { setActiveTab(p.dataset.panel); activateSection(p); }
          updateArrows();
        });
      }, { passive: true });
    }
    // Arrow enable/disable also tracks the inner photo scroll.
    if (photoGrid) on(photoGrid, 'scroll', () => updateArrows(), { passive: true });
    on(track, 'scroll', () => updateArrows(), { passive: true });
  }

  // ---- Lightbox ----
  function renderLightboxThumbs() {
    if (!lbThumbs) return;
    lbThumbs.innerHTML = lbSet.map((p, i) => {
      const video = isVideoItem(p);
      const cls = `ap-lightbox__thumb${i === lightboxIndex ? ' is-active' : ''}${video ? ' is-video' : ''}`;
      const overlay = video ? '<span class="ap-lightbox__thumb-play" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></span>' : '';
      // Eager, and opt the thumb OUT of any page-cache lazy-loader
      // (data-skip-lazy / .no-lazyload): the strip is small, already on-screen
      // when the lightbox opens, and its URLs come from data-full — deferring
      // them only risks the "later thumbs never load" bug this file guards.
      const img = p.thumb ? `<img class="no-lazyload" src="${p.thumb}" alt="" loading="eager" decoding="async" data-skip-lazy data-no-lazy="1">` : '';
      return `
      <button class="${cls}" type="button" data-lb-index="${i}" aria-label="${video ? 'Video' : 'Imagine ' + (i + 1)}">
        ${img}${overlay}
      </button>`;
    }).join('');
    const active = qs('.ap-lightbox__thumb.is-active', lbThumbs);
    if (active) active.scrollIntoView({ inline: 'center', block: 'nearest' });
  }

  function syncLightboxThumb() {
    if (!lbThumbs) return;
    qsa('.ap-lightbox__thumb', lbThumbs).forEach((t, i) => {
      t.classList.toggle('is-active', i === lightboxIndex);
    });
    const active = qs('.ap-lightbox__thumb.is-active', lbThumbs);
    if (active) active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }

  // Warm a full-size image in the background so navigating to it is instant.
  function preloadFull(url) {
    if (!url || lbPreloaded.has(url)) return;
    lbPreloaded.add(url);
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  }

  // Progressive display: show the already-loaded grid thumbnail at once (no
  // blank stage), swap to the full-res as soon as it decodes, and preload the
  // neighbours so the next/prev click has nothing to wait for.
  function showLightboxImage() {
    const item = lbSet[lightboxIndex];
    if (!lbImage || !item) return;

    // Video slide: swap the image stage for the (lazily built) autoplay iframe.
    if (isVideoItem(item)) {
      lbCurrentFull = '';
      if (lbZoomCtl) lbZoomCtl.hidden = true; // never zoom the video
      mountVideoFrame();
      return;
    }
    // Any non-video slide → make sure the video iframe is gone (stops audio).
    if (lbZoomCtl) lbZoomCtl.hidden = false;
    unmountVideoFrame();

    const full = item.full;
    lbCurrentFull = full;

    if (lbPreloaded.has(full)) {
      lbImage.src = full;                 // cached → instant
      lbImage.classList.remove('is-loading');
    } else {
      if (item.thumb && item.thumb !== full) {
        lbImage.src = item.thumb;         // instant low-res stand-in
        lbImage.classList.add('is-loading');
      }
      const hi = new Image();
      hi.decoding = 'async';
      hi.onload = () => {
        lbPreloaded.add(full);
        if (lbCurrentFull === full) {     // ignore if the user already moved on
          lbImage.src = full;
          lbImage.classList.remove('is-loading');
        }
      };
      hi.onerror = () => { if (lbCurrentFull === full) lbImage.classList.remove('is-loading'); };
      hi.src = full;
    }

    const n = lbSet.length;
    if (n > 1) {
      preloadFull(lbSet[(lightboxIndex + 1) % n].full);
      preloadFull(lbSet[(lightboxIndex - 1 + n) % n].full);
    }
  }

  function openLightbox(set, i) {
    if (!lightbox || !lbImage || !set.length) return;
    lbSet = set;
    lightboxIndex = i;
    // Open the dialog FIRST, then render the current slide. A <dialog> is
    // display:none until shown, so mounting the video iframe before showModal()
    // would build the YouTube player inside a hidden frame → it never autoplays.
    // Showing the modal first (still within the user's click gesture) lets the
    // freshly mounted iframe autoplay with sound.
    if (typeof lightbox.showModal === 'function') lightbox.showModal();
    else lightbox.setAttribute('open', '');
    lightbox.style.transition = ''; // clear any leftover drag-to-dismiss fade
    lightbox.style.opacity = '';
    resetZoom(); // always open fitted at 1x
    showLightboxImage();
    if (lbCounter) lbCounter.textContent = `${lightboxIndex + 1} / ${lbSet.length}`;
    renderLightboxThumbs();
    lockScroll(true);
    releaseFocus = trapFocus(lightbox);
  }
  function closeLightbox() {
    if (!lightbox) return;
    resetZoom(); // drop any zoom/pan so re-opening starts fitted
    lightbox.style.transition = ''; // clear any drag-to-dismiss fade
    lightbox.style.opacity = '';
    unmountVideoFrame(); // stop any playing video before the modal closes
    if (typeof lightbox.close === 'function') lightbox.close();
    else lightbox.removeAttribute('open');
    lockScroll(false);
    if (releaseFocus) { releaseFocus(); releaseFocus = null; }
  }
  function lightboxGoTo(idx) {
    if (!lbSet.length) return;
    lightboxIndex = ((idx % lbSet.length) + lbSet.length) % lbSet.length;
    resetZoom(); // never carry a zoomed/panned view across slides
    showLightboxImage();
    if (lbCounter) lbCounter.textContent = `${lightboxIndex + 1} / ${lbSet.length}`;
    syncLightboxThumb();
  }
  function lightboxStep(d) { lightboxGoTo(lightboxIndex + d); }

  // ---- Lightbox zoom + pan ----------------------------------------------
  // Zoom the active photo (never the video slide) with double-tap/click,
  // pinch, and wheel; pan with a one-finger/mouse drag while zoomed. Applied
  // as a GPU-friendly `translate(...) scale(...)` on the <img>, anchored at the
  // stage centre (transform-origin: center) so translate offsets are measured
  // from the fitted image's centre. All state below lives at 1x when idle so
  // the existing swipe/close gestures on the stage behave exactly as before.
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  const ZOOM_DBL = 2.5; // double-tap target scale
  let zScale = 1, zX = 0, zY = 0; // current scale + translate (px, pre-scale space)
  const isZoomed = () => zScale > 1.001;

  function applyZoom(animate) {
    if (!lbImage) return;
    // Keep the CSS filter (blur→focus) transition intact; only vary transform.
    lbImage.style.transition = animate
      ? 'transform .22s var(--ap-ease, ease), filter .3s var(--ap-ease, ease)'
      : 'transform 0s, filter .3s var(--ap-ease, ease)';
    lbImage.style.transform = `translate(${zX}px, ${zY}px) scale(${zScale})`;
    lbImage.classList.toggle('is-zoomed', isZoomed());
    // The stage is a fully custom gesture surface (swipe-to-navigate, pinch/pan
    // zoom and vertical drag-to-dismiss are all handled in JS), so the browser
    // never gets to claim the touch gesture — at any zoom level.
    if (stage) stage.style.touchAction = 'none';
  }

  function resetZoom(animate) {
    zScale = 1; zX = 0; zY = 0;
    if (lbImage) applyZoom(!!animate);
  }

  // Clamp the pan so the (scaled) image can never be dragged past its own
  // edges — i.e. no empty gap appears between the visible photo and the stage
  // bounds. The <img> box fills the stage but `object-fit: contain` letterboxes
  // the actual pixels, so bounds are computed from the *contained content* size
  // (via naturalWidth/Height), scaled by zScale — not the raw element box.
  function contentSize() {
    if (!lbImage || !stage) return { w: 0, h: 0 };
    const host = stage.getBoundingClientRect();
    const nw = lbImage.naturalWidth || host.width;
    const nh = lbImage.naturalHeight || host.height;
    const fit = Math.min(host.width / nw, host.height / nh) || 1; // contain scale at 1x
    return { w: nw * fit * zScale, h: nh * fit * zScale, host };
  }
  function clampPan() {
    if (!lbImage || !stage) return;
    const { w, h, host } = contentSize();
    if (!host) return;
    const maxX = Math.max(0, (w - host.width) / 2);
    const maxY = Math.max(0, (h - host.height) / 2);
    zX = Math.min(maxX, Math.max(-maxX, zX));
    zY = Math.min(maxY, Math.max(-maxY, zY));
  }

  // Zoom toward a point (client coords). Keeps that point visually anchored by
  // adjusting the translate as the scale changes, then clamps within bounds.
  function zoomToPoint(clientX, clientY, nextScale, animate) {
    if (!lbImage) return;
    nextScale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, nextScale));
    const host = stage ? stage.getBoundingClientRect() : lbImage.getBoundingClientRect();
    // Point relative to the stage centre (the transform-origin).
    const cx = host.left + host.width / 2;
    const cy = host.top + host.height / 2;
    const px = clientX - cx;
    const py = clientY - cy;
    if (nextScale <= ZOOM_MIN) {
      resetZoom(animate);
      return;
    }
    // Solve so the content point under the cursor stays put across the scale
    // change: new translate = point - (point - oldTranslate) * (new/old).
    const ratio = nextScale / zScale;
    zX = px - (px - zX) * ratio;
    zY = py - (py - zY) * ratio;
    zScale = nextScale;
    clampPan();
    applyZoom(animate);
  }

  // Zoom toward the stage centre (used by the toolbar +/- buttons).
  function zoomFromCenter(nextScale) {
    if (!stage || isVideoItem(lbSet[lightboxIndex])) return;
    const host = stage.getBoundingClientRect();
    zoomToPoint(host.left + host.width / 2, host.top + host.height / 2, nextScale, true);
  }

  // ---- Tabs (quick-jump navigation) ----
  // A tab click scroll-snaps the track to its section; the active-tab highlight
  // is then kept in sync by the scroll observer above. The photo tab also
  // resets the inner mosaic to its start so "Fotografii" always lands on photo 1.
  delegate(root, 'click', '.ap-cd-gallery-tab', (e, btn) => {
    e.preventDefault();
    const tab = btn.dataset.tab;
    setActiveTab(tab); // immediate feedback; observer confirms after the scroll
    const panel = qs(`.ap-cd-gallery-panel[data-panel="${tab}"]`, track || root);
    if (tab === 'body' && photoGrid) photoGrid.scrollTo({ left: 0, behavior: 'smooth' });
    scrollToSection(panel);
    updateArrows();
  });

  // Tile click → open the lightbox seeded with that grid's photo set, with the
  // car video appended as the final slide (when present).
  delegate(root, 'click', '.ap-cd-gallery-tile', (e, tile) => {
    e.preventDefault();
    const grid = tile.closest('[data-role="gallery-grid"]');
    openLightbox(withVideo(photosOf(grid)), Number(tile.dataset.index) || 0);
  });

  // Video cover facade → open the lightbox straight at the video slide (last),
  // which autoplays. Seed the set with the car's photos so paging back reaches
  // them; if there are no photos, the video is a lone slide.
  delegate(root, 'click', '[data-action="open-video"]', (e) => {
    e.preventDefault();
    if (!videoSlide) return;
    const set = withVideo(photosOf(photoGrid));
    openLightbox(set, set.length - 1);
  });

  if (lightbox) {
    delegate(lightbox, 'click', '[data-action="close-lightbox"]', closeLightbox);
    delegate(lightbox, 'click', '[data-action="prev-lightbox"]', () => lightboxStep(-1));
    delegate(lightbox, 'click', '[data-action="next-lightbox"]', () => lightboxStep(1));
    delegate(lightbox, 'click', '.ap-lightbox__thumb', (e, t) => lightboxGoTo(Number(t.dataset.lbIndex)));
    // Toolbar zoom buttons → step zoom toward the stage centre.
    delegate(lightbox, 'click', '[data-action="zoom-in"]', () => zoomFromCenter(zScale * 1.5));
    delegate(lightbox, 'click', '[data-action="zoom-out"]', () => zoomFromCenter(zScale / 1.5));
    on(lightbox, 'click', (e) => { if (e.target === lightbox) closeLightbox(); });
    on(lightbox, 'cancel', (e) => { e.preventDefault(); closeLightbox(); });
    on(lightbox, 'close', () => { unmountVideoFrame(); lockScroll(false); if (releaseFocus) { releaseFocus(); releaseFocus = null; } });
    on(document, 'keydown', (e) => {
      if (!lightbox.open) return;
      if (e.key === 'ArrowRight') lightboxStep(1);
      if (e.key === 'ArrowLeft') lightboxStep(-1);
    });

    // Stage gestures: at 1x → swipe = prev/next, vertical swipe / backdrop tap =
    // close (unchanged). When zoomed → the same drag PANS the image instead, and
    // pinch/wheel/double-tap drive zoom. `stage` was hoisted above so the zoom
    // helpers (applyZoom/clampPan/zoomToPoint) share it.
    if (stage) {
      const HSWIPE = 40;
      // Whether the active slide can be zoomed (not the video slide).
      const zoomable = () => !isVideoItem(lbSet[lightboxIndex]);

      // ---- Pinch: track live pointers so two fingers = pinch-zoom -----------
      const pointers = new Map(); // pointerId -> { x, y }
      let pinchStartDist = 0, pinchStartScale = 1, pinchMid = { x: 0, y: 0 };
      const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
      const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

      // ---- Single-pointer drag: swipe (1x) or pan (zoomed) -----------------
      let startX = 0, startY = 0, dx = 0, dy = 0, dragging = false;
      let panStartX = 0, panStartY = 0; // zX/zY at drag start (pan mode)
      let panning = false;

      // ---- Vertical drag-to-dismiss (1x) -----------------------------------
      // A mostly-vertical drag anywhere on the stage (including the dark area
      // around the image) pulls the photo with the finger and fades the chrome;
      // releasing past a short threshold — or a quick flick — closes the
      // lightbox, otherwise it springs back. The "usual" mobile lightbox feel.
      let axis = null;         // 'v' | 'h' once a 1x drag commits to a direction
      let dismissing = false;  // a vertical drag-to-dismiss is under way
      let dragStartT = 0;      // timestamp for flick-velocity detection
      const DISMISS_CLOSE = 90;  // px past which release dismisses
      const DISMISS_FADE = 0.6;  // max chrome fade reached during the drag
      function applyDismiss(offY) {
        if (!lbImage || !lightbox) return;
        const prog = Math.min(1, Math.abs(offY) / DISMISS_CLOSE);
        const scale = 1 - Math.min(0.16, Math.abs(offY) / 1400);
        lbImage.style.transition = 'transform 0s';
        lbImage.style.transform = `translate(0px, ${offY}px) scale(${scale})`;
        lightbox.style.transition = 'none';
        lightbox.style.opacity = String(1 - prog * DISMISS_FADE);
      }
      function endDismiss(close) {
        dismissing = false;
        if (close) { closeLightbox(); return; }
        // Spring the photo back to the fitted view + restore full chrome.
        if (lbImage) applyZoom(true); // transform → identity, animated
        if (lightbox) {
          lightbox.style.transition = 'opacity .22s var(--ap-ease, ease)';
          lightbox.style.opacity = '1';
        }
      }

      // ---- Double-tap / double-click toggle --------------------------------
      let lastTapT = 0, lastTapX = 0, lastTapY = 0;
      const DOUBLE_MS = 300, DOUBLE_SLOP = 30;
      function toggleZoomAt(x, y) {
        if (!zoomable()) return;
        if (isZoomed()) resetZoom(true);
        else zoomToPoint(x, y, ZOOM_DBL, true);
      }

      on(stage, 'pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('[data-action]')) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        // Two pointers down → begin (or continue) a pinch; suspend drag/swipe.
        if (pointers.size === 2 && zoomable()) {
          const [p1, p2] = [...pointers.values()];
          pinchStartDist = dist(p1, p2) || 1;
          pinchStartScale = zScale;
          pinchMid = mid(p1, p2);
          dragging = false; panning = false;
          try { stage.setPointerCapture(e.pointerId); } catch (_) {}
          return;
        }
        if (pointers.size !== 1) return;

        // Double-tap (touch/pen) detection on the down event.
        const now = Date.now();
        if (e.pointerType !== 'mouse' &&
            now - lastTapT < DOUBLE_MS &&
            Math.abs(e.clientX - lastTapX) < DOUBLE_SLOP &&
            Math.abs(e.clientY - lastTapY) < DOUBLE_SLOP) {
          toggleZoomAt(e.clientX, e.clientY);
          lastTapT = 0; // consume
          dragging = false; panning = false;
          return;
        }
        lastTapT = now; lastTapX = e.clientX; lastTapY = e.clientY;

        startX = e.clientX; startY = e.clientY; dx = 0; dy = 0; dragging = true;
        axis = null; dismissing = false; dragStartT = now;
        if (isZoomed()) {
          panning = true;
          panStartX = zX; panStartY = zY;
          lbImage.style.transition = 'transform 0s';
          try { stage.setPointerCapture(e.pointerId); } catch (_) {}
        } else {
          panning = false;
        }
      });

      on(stage, 'pointermove', (e) => {
        if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        // Active pinch (2 pointers).
        if (pointers.size === 2 && pinchStartDist) {
          const [p1, p2] = [...pointers.values()];
          const d = dist(p1, p2) || 1;
          const m = mid(p1, p2);
          const next = pinchStartScale * (d / pinchStartDist);
          zoomToPoint(m.x, m.y, next, false);
          e.preventDefault();
          return;
        }

        if (!dragging) return;
        dx = e.clientX - startX; dy = e.clientY - startY;

        if (panning) {
          // Drag pans the zoomed image within bounds; do not scroll/close.
          zX = panStartX + dx;
          zY = panStartY + dy;
          clampPan();
          applyZoom(false);
          e.preventDefault();
          return;
        }
        // 1x: commit to an axis on first meaningful movement. A vertical drag
        // pulls the photo away for drag-to-dismiss; horizontal stays swipe-to-
        // navigate (resolved on release, no live tracking).
        if (!axis && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
          axis = Math.abs(dy) > Math.abs(dx) ? 'v' : 'h';
        }
        if (axis === 'v') {
          dismissing = true;
          e.preventDefault();
          applyDismiss(dy);
        }
      }, { passive: false }); // pinch/pan/dismiss call preventDefault()

      const endDrag = (e) => {
        // Remove the lifted pointer; if a pinch just ended, settle and bail.
        const wasPinch = pointers.size === 2;
        if (e) pointers.delete(e.pointerId);
        if (wasPinch) {
          pinchStartDist = 0;
          if (!isZoomed()) resetZoom(true); // snap back if pinched below 1x
          dragging = false; panning = false;
          return;
        }
        if (!dragging) return;
        dragging = false;

        if (panning) { panning = false; return; } // panned, no swipe/close

        // Vertical drag-to-dismiss: close on a long-enough pull OR a quick flick,
        // otherwise spring the photo back into place.
        if (dismissing) {
          const dt = Math.max(1, Date.now() - dragStartT);
          const flick = Math.abs(dy) > 40 && (Math.abs(dy) / dt) > 0.6;
          endDismiss(Math.abs(dy) > DISMISS_CLOSE || flick);
          axis = null;
          return;
        }

        // At 1x: horizontal swipe navigates; a plain tap on the dark area closes.
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        axis = null;
        if (absDx > HSWIPE && absDx > absDy) { lightboxStep(dx > 0 ? -1 : 1); return; }
        if (absDx < 6 && absDy < 6 && e && e.target === stage) closeLightbox();
      };
      on(stage, 'pointerup', endDrag);
      on(stage, 'pointercancel', endDrag);
      on(stage, 'pointerleave', (e) => {
        // Only end on true leave of the stage (mouse), not per-pointer noise.
        if (e && e.pointerType === 'mouse') endDrag(e);
      });

      // Double-click (desktop mouse) toggles zoom toward the cursor.
      on(stage, 'dblclick', (e) => {
        if (e.target.closest('[data-action]')) return;
        e.preventDefault();
        toggleZoomAt(e.clientX, e.clientY);
      });

      // Wheel / trackpad zoom toward the cursor. Only intercept when the slide
      // is zoomable; keep the page from scrolling underneath.
      on(stage, 'wheel', (e) => {
        if (!zoomable()) return;
        if (e.target.closest('[data-action]')) return;
        e.preventDefault();
        const factor = Math.exp(-e.deltaY * 0.0015); // smooth multiplicative step
        zoomToPoint(e.clientX, e.clientY, zScale * factor, false);
      }, { passive: false });
    }
  }

  // Activate the section that is visible on load (the first one in the track,
  // e.g. a video/map for a car with no photos) so its iframe lazy-loads without
  // waiting for a tab click. When IntersectionObserver isn't available the
  // scroll-sync fallback won't fire on load, so seed the first section here too.
  const firstSection = sections[0] || qs('.ap-cd-gallery-panel.is-active', root);
  if (firstSection) {
    activateSection(firstSection);
    setActiveTab(firstSection.dataset.panel);
  }

  // ---- Warm the mosaic photos so horizontal swiping never waits on a lazy
  // fetch. The first few tiles render loading="eager" server-side; the rest are
  // lazy. Once the gallery is in view (or the user first touches/scrolls the
  // mosaic), preload every remaining thumbnail into cache during idle time, so
  // by the time a tile scrolls into view it decodes instantly. Deferred to idle
  // so it never competes with the hero/LCP, and skipped under Data Saver. ----
  (function warmMosaic() {
    const conn = navigator.connection;
    if (conn && conn.saveData) return; // respect Data Saver
    const tiles = qsa('.ap-cd-gallery-tile img', root);
    if (tiles.length <= 1) return;
    let warmed = false;
    const warm = () => {
      if (warmed) return;
      warmed = true;
      const run = () => tiles.forEach((img) => {
        // Use the real (un-lazyloaded) URL so warming fetches the actual photo,
        // not a placeholder left on deferred tiles by WP Rocket LazyLoad.
        const src = realImgSrc(img) || img.currentSrc;
        if (!src) return;
        const pre = new Image();
        pre.decoding = 'async';
        pre.src = src; // hits the browser cache; the lazy tile then loads instantly
      });
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 1500 });
      else setTimeout(run, 300);
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries, obs) => {
        if (entries.some((e) => e.isIntersecting)) { obs.disconnect(); warm(); }
      }, { rootMargin: '300px' });
      io.observe(root);
    } else {
      warm();
    }
    // Also warm immediately on the first sign of intent to browse photos.
    if (photoGrid) {
      const onIntent = () => warm();
      on(photoGrid, 'touchstart', onIntent, { passive: true, once: true });
      on(photoGrid, 'scroll', onIntent, { passive: true, once: true });
      on(photoGrid, 'pointerenter', onIntent, { passive: true, once: true });
    }
  })();

  // Tiles are server-rendered with the car's real photos - just size arrows.
  updateArrows();
}

export function initSpecsTabs() {
  const root = qs('[data-component="specs"]');
  if (!root) return;
  delegate(root, 'click', '.ap-cd-specs-tab', (e, btn) => {
    const tab = btn.dataset.tab;
    qsa('.ap-cd-specs-tab', root).forEach((c) => {
      c.classList.toggle('is-active', c === btn);
      c.setAttribute('aria-selected', String(c === btn));
    });
    qsa('.ap-cd-specs-panel', root).forEach((p) => {
      p.classList.toggle('is-active', p.dataset.panel === tab);
    });
  });

  // "Vezi toate caracteristicile" - collapse/expand the spec rows past
  // the first 10. The grid hides them via CSS; this toggles the
  // .is-expanded class on the parent panel.
  delegate(root, 'click', '[data-action="toggle-specs"]', (e, btn) => {
    const panel = btn.closest('.ap-cd-specs-panel');
    if (!panel) return;
    const expanded = panel.classList.toggle('is-expanded');
    const label = qs('[data-role="specs-toggle-label"]', btn);
    if (label) label.textContent = expanded ? t('see_less', 'Vezi mai puțin') : t('see_all_specs', 'Vezi toate caracteristicile');
  });

  // The "Informații" toggle now lives in components/collapsible-text.js, which
  // owns every editorial block (it renders expanded and clamps after the first
  // scroll). Handling it here too would fight that module over the same class.
}
